import axios from 'axios';
import { fetchAuthSession } from 'aws-amplify/auth';
import {
  transformThreadsResponse,
  transformThreadMessagesResponse,
  transformPendingMessagesResponse,
} from './QueryTransforms';
import type {
  ThreadsResponse,
  ThreadMessagesResponse,
  PendingMessagesResponse,
  ApproveMessagePayload,
} from './QueryTypes';
import type {
  ConsentArtefactRequest,
  RecordConsentResponse,
  ConsentStateResponse,
  WithdrawConsentRequest,
  WithdrawConsentResponse,
  ServiceSelectionRequest,
  ServiceSelectionResponse,
  CookieConsentRequest,
  CookieConsentResponse,
  CookieConsentStateResponse,
} from './ConsentTypes';
import type {
  NotificationsResponse,
  MarkNotificationReadResponse,
  MarkAllNotificationsReadResponse,
  UpdateDoctorPayload,
  UpdateNursePayload,
  UpdateStaffResponse,
  DeactivateStaffResponse,
  SupportTicketListResponse,
  SupportTicketReplyResponse,
  SupportTicketStatus,
  UpdateSupportTicketPayload,
  UpdateSupportTicketResponse,
  CancelVideoCallResponse,
  RescheduleVideoCallResponse,
} from './PlatformTypes';

export interface AdminResetPasswordResponse {
  message: string;
  email: string;
  newPassword: string;
  userStatus: string;
  emailSent: boolean;
}

const API_URL = process.env.REACT_APP_API_URL;

class APIService {

  async getHeaders() {
    const res = await fetchAuthSession();
    const config = {
      headers: {
        'Content-Type': 'application/json',
        authorizationToken: `${res.tokens.idToken}`
      }
    };
    return config;
  }

  async getPatients() {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}patients`, header)
      .then((res) => res.data);

  }

  async getAllUsers(params: { limit?: number; nextToken?: string; userType?: string }) {
    const header = await this.getHeaders();

    const queryParams = new URLSearchParams();
    if (params.limit !== undefined) queryParams.append('limit', String(params.limit));
    if (params.nextToken) queryParams.append('nextToken', params.nextToken);
    if (params.userType) queryParams.append('userType', params.userType);

    return axios
      .get(`${API_URL}users?${queryParams.toString()}`, header)
      .then((res) => res.data);
  }

  async getPatientReports(patientId: string | null, status?: 'pending_review' | 'approved' | 'rejected') {
    const header = await this.getHeaders();
    // The list is paginated: each response carries `nextToken` while more pages
    // remain. Reading only the first page hid newer reports — the admin Pending
    // Reports queue showed 4 of 6 on live — so every page is fetched here.
    const reports: any[] = [];
    let nextToken: string | undefined;
    let pages = 0;
    do {
      const params = new URLSearchParams();
      if (patientId) params.append('patient_id', patientId);
      if (status) params.append('status', status);
      if (nextToken) params.append('nextToken', nextToken);
      const query = params.toString();
      const data = await axios
        .get(`${API_URL}patients/reports${query ? `?${query}` : ''}`, header)
        .then((res) => res.data);
      reports.push(...(data?.reports ?? []));
      nextToken = data?.nextToken || undefined;
      pages += 1;
    } while (nextToken && pages < 50);
    return { reports, count: reports.length };
  }

  async getDicomStudies(patientId?: string, statusFilter?: 'uploading' | 'completed' | 'failed') {
    const header = await this.getHeaders();
    const params = new URLSearchParams();
    if (patientId) params.append('patient_id', patientId);
    if (statusFilter) params.append('status_filter', statusFilter);
    const query = params.toString();
    return axios.get(`${API_URL}dicom/studies${query ? `?${query}` : ''}`, header).then((res) => res.data);
  }

  async getViewerToken(studyInstanceUID: string): Promise<import('src/utils/viewerSession').ViewerTokenResponse> {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}dicom/viewer-token?study_instance_uid=${encodeURIComponent(studyInstanceUID)}`, header)
      .then((res) => {
        const d = res.data;
        // Guide response: { datastoreId, imageSetId, studyInstanceUid }
        // Normalise: handle both camelCase (guide) and snake_case (legacy) backend shapes
        return {
          datastoreId: d.datastoreId ?? d.datastore_id ?? '',
          imageSetId: d.imageSetId ?? d.image_set_id ?? '',
          studyInstanceUid: d.studyInstanceUid ?? d.study_instance_uid ?? studyInstanceUID,
        };
      });
  }

  async getPatientTransactions() {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}patients/transactions`, {}, header)
      .then((res) => res.data);
  }

  async getPatientProfile(field: string, patientId?: string) {
    const header = await this.getHeaders();
    const params = new URLSearchParams();
    if (field) params.append('fields', field);
    if (patientId) params.append('patient_id', patientId);
    const query = params.toString();
    return axios.get(`${API_URL}patients/profile${query ? `?${query}` : ''}`, header);
  }

  async getPatientProfileById(patientId: string, fields?: string[]) {
    const header = await this.getHeaders();
    const params = new URLSearchParams();
    if (fields?.length) {
      params.append("fields", fields.join(","));
    }
    params.append("patient_id", patientId);

    return axios
      .get(`${API_URL}patients/profile?${params.toString()}`, header)
      .then((res) => res.data);
  }

  async getPatientUploads(patientId: string) {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}medical-docs/get-docs?patient_id=${patientId}`, header)
      .then((res) => {
        const data = res.data;
        // API returns { documents, studies, docCount, studyCount }
        // Normalise: expose `medicalDocs` alias for backward-compat with legacy callers
        return {
          ...data,
          medicalDocs: data.documents ?? data.medicalDocs ?? [],
        };
      });
  }

  async updatePatientProfile(patientDetails: any, patientId?: string) {
    const header = await this.getHeaders();
    const endpoint = patientId
      ? `${API_URL}patients/update_details?patient_id=${patientId}`
      : `${API_URL}patients/update_details`;
    return axios.post(endpoint, patientDetails, header);
  }

  async updateUserType(PK: string, userType: string) {
    const header = await this.getHeaders();
    return axios.put(`${API_URL}patients/user_type`, { PK, userType }, header);
  }

  async getMedicalDocs(ItemType?: string, patientId?: string) {
    const header = await this.getHeaders();
    const itemTypeParam = (!ItemType || ItemType.trim() === '')
      ? 'Documents,MedicalHistoryDocument,FamilyMedicalHistoryDocument,SurgicalHistoryDocument,ImmunizationRecordsDocument,AllergiesDocument,ChronicConditionsDocument,PreviousDiagnosesAndTreatmentsDocument'
      : ItemType;
    const url = patientId
      ? `${API_URL}medical-docs/get-docs?ItemType=${itemTypeParam}&patient_id=${patientId}`
      : `${API_URL}medical-docs/get-docs?ItemType=${itemTypeParam}`;
    return axios.get(url, header).then((res) => {
      const data = res.data;
      // API returns { documents, studies, docCount, studyCount }
      // Normalise: expose `medicalDocs` alias for backward-compat with legacy callers
      return {
        ...data,
        medicalDocs: data.documents ?? data.medicalDocs ?? [],
      };
    });
  }

  async getDocsAndStudies(patientId?: string): Promise<{
    documents: any[];
    studies: any[];
    docCount: number;
    studyCount: number;
  }> {
    const header = await this.getHeaders();
    const url = patientId
      ? `${API_URL}medical-docs/get-docs?patient_id=${patientId}`
      : `${API_URL}medical-docs/get-docs`;
    return axios.get(url, header).then((res) => ({
      documents: res.data.documents ?? [],
      studies: res.data.studies ?? [],
      docCount: res.data.docCount ?? 0,
      studyCount: res.data.studyCount ?? 0,
    }));
  }

  async getMedicalDocsCount(patientId?: string, role?: string) {
    const header = await this.getHeaders();
    const canUsePatientId = !!patientId && (role === 'admin' || role === 'doctor' || role === 'nurse');
    const url = canUsePatientId
      ? `${API_URL}medical-docs/get-docs-count?patient_id=${patientId}`
      : `${API_URL}medical-docs/get-docs-count`;
    return axios
      .get(url, header)
      .then((res) => res.data);
  }

  async getMedicalDocPresignedUrl(
    extension: string,
    contentType: string,
    itemType: string,
    patientId?: string
  ) {
    const header = await this.getHeaders();
    // patient_id is passed as a query param only — not duplicated in the body
    const body = { extension, contentType, itemType };
    const url = patientId
      ? `${API_URL}medical-docs/presigned-url-upload?patient_id=${patientId}`
      : `${API_URL}medical-docs/presigned-url-upload`;
    return axios
      .post(url, body, header)
      .then((res) => res.data);
  }

  async uploadMedicalDoc(preSignedUrl: string, formData: FormData): Promise<void> {
    const res = await fetch(preSignedUrl, {
      method: 'POST',
      body: formData,
    });
    // Guide step 2: POST directly to S3 — surface failures so callers can toast
    if (res.status !== 200 && res.status !== 204) {
      const body = await res.text().catch(() => '');
      throw new Error(`S3 upload failed (HTTP ${res.status}): ${body}`);
    }
  }

  async deleteMedicalDoc(preSignedUrl: string) {
    const header = await this.getHeaders();
    const data = {
      presigned_url: preSignedUrl
    };
    return axios
      .post(`${API_URL}medical-docs/delete-document`, data, header)
      .then((res) => res.data);
  }

  async deleteMedicalDocByKey(s3Key: string) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}medical-docs/delete`, { s3_key: s3Key }, header)
      .then((res) => res.data);
  }

  async createNotes(note: string, patientId?: string) {
    const header = await this.getHeaders();
    const data = { note };
    const endpoint = patientId
      ? `${API_URL}notes/create-note?patient_id=${patientId}`
      : `${API_URL}notes/create-note`;

    return axios
      .post(endpoint, data, header)
      .then((res) => res.data);
  }

  async getNotes(patientId?: string) {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}notes/get-notes?patient_id=${patientId}`, header)
      .then((res) => res.data);
  }

  async editNote(noteId: string, updatedNote: string, patientId?: string) {
    const header = await this.getHeaders();
    const data = {
      noteId,
      Note: updatedNote
    };
    const endpoint = patientId
      ? `${API_URL}notes/edit-note?patient_id=${patientId}`
      : `${API_URL}notes/edit-note`;

    return axios
      .put(endpoint, data, header)
      .then((res) => res.data);
  }

  async deleteNote(noteId: string) {
    const header = await this.getHeaders();
    const data = { noteId };

    return axios
      .delete(`${API_URL}notes/delete-note`, { data, ...header })
      .then((res) => res.data);
  }

  async updateHealthProviderDetails(email: string) {
    const header = await this.getHeaders();
    const data = {
      Email: email
    };
    return axios
      .post(`${API_URL}health_provider/update_details`, data, header)
      .then((res) => res.data);
  }

  async createStripePayment(products: { price_id: any }[]) {
    const header = await this.getHeaders();
    const data = { products };

    return axios
      .post(`${API_URL}stripe/payment`, data, header)
      .then((res) => res.data);
  }

  async sendProfile(patientId?: string) {
    const header = await this.getHeaders();
    const url = patientId
      ? `${API_URL}patients/send_profile?patient_id=${patientId}`
      : `${API_URL}patients/send_profile`;
    return axios
      .post(url, {}, header)
      .then((res) => res.data)
  }

  async createSupportTicket(category: string, message: string) {
    const header = await this.getHeaders();
    const data = { category, message };

    return axios
      .post(`${API_URL}support/create`, data, header)
      .then((res) => res.data);
  }

  async getSupportTickets() {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}support/get`, header)
      .then((res) => res.data);
  }

  async createDoctor(email: string, firstName: string, lastName: string, speciality: string) {
    const header = await this.getHeaders();
    const data = { email, firstName, lastName, speciality };

    return axios
      .post(`${API_URL}admin/create-doctor`, data, header)
      .then((res) => res.data);
  }

  async createNurse(payload: {
    email: string;
    firstName: string;
    middleName?: string;
    lastName: string;
    department?: string;
    specialization?: string;
  }) {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}admin/create-nurse`, payload, header)
      .then((res) => res.data);
  }

  async adminResetPassword(email: string): Promise<AdminResetPasswordResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}admin/reset-password`, { email }, header)
      .then((res) => res.data);
  }

  async getDoctors() {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}admin/doctors`, header)
      .then((res) => res.data);
  }

  async getNurses() {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}admin/nurses`, header)
      .then((res) => res.data);
  }

  async assignPatientToDoctor(
    patientId: string,
    doctorIds: string | string[],
    role: 'admin' | 'nurse' = 'admin'
  ) {
    const header = await this.getHeaders();
    const doctorIdArray = Array.isArray(doctorIds) ? doctorIds : [doctorIds];
    const data = { doctorId: doctorIdArray, action: "assign" };
    // Each role has its own endpoint: the admin one refuses nurses with a 403.
    const prefix = role === 'nurse' ? 'nurse' : 'admin';

    return axios
      .post(`${API_URL}${prefix}/patients/${patientId}/assign`, data, header)
      .then((res) => res.data);
  }

  async getUnassignedPatients() {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}admin/patients/unassigned`, header)
      .then((res) => res.data);
  }

  async getPatientTransactionsById(patientId: string) {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}admin/patients/${patientId}/transactions`, header)
      .then((res) => res.data);
  }

  async getAssignedPatients() {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}doctor/patients`, header)
      .then((res) => res.data);
  }

  async createPatientReport(patientId: string, reportContent: string, language?: 'en' | 'bg') {
    const header = await this.getHeaders();
    const data: Record<string, any> = { report_content: reportContent };
    if (language) data.language = language;

    return axios
      .post(`${API_URL}doctor/patients/${patientId}/report`, data, header)
      .then((res) => res.data);
  }

  async createQuery(patientId: string, query: string, isActingAsPatient?: boolean, subject?: string) {
    const header = await this.getHeaders();
    const data: Record<string, any> = { patient_id: patientId, query };
    if (isActingAsPatient) data.is_acting_as_patient = true;
    if (subject) data.subject = subject;
    return axios
      .post(`${API_URL}queries/create`, data, header)
      .then((res) => res.data);
  }

  async getQueries(patientId: string) {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}queries?patient_id=${patientId}`, header)
      .then((res) => res.data);
  }

  async respondToQuery(queryId: string, responseText: string, patientId?: string) {
    const header = await this.getHeaders();
    const data: Record<string, any> = { query_id: queryId, response: responseText };
    if (patientId) data.patient_id = patientId;
    return axios
      .post(`${API_URL}queries/respond`, data, header)
      .then((res) => res.data);
  }

  async getPendingQueriesCount() {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}queries?count_only=true`, header)
      .then((res) => res.data);
  }

  async getPendingQueries() {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}queries?approved=false`, header)
      .then((res) => res.data);
  }

  async approveQuery(payload: any) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}queries/approve`, payload, header)
      .then((res) => res.data);
  }

  async getThreads(params?: { patient_id?: string; query_type?: string }): Promise<ThreadsResponse> {
    const header = await this.getHeaders();
    let url = `${API_URL}queries?mode=threads`;

    if (params?.patient_id) {
      url += `&patient_id=${encodeURIComponent(params.patient_id)}`;
    }
    if (params?.query_type) {
      url += `&query_type=${encodeURIComponent(params.query_type)}`;
    }

    return axios
      .get(url, header)
      .then((res) => transformThreadsResponse(res.data));
  }

  async getThreadMessages(threadId: string, patientId?: string): Promise<ThreadMessagesResponse> {
    const header = await this.getHeaders();
    let url = `${API_URL}queries?mode=messages&thread_id=${encodeURIComponent(threadId)}`;
    if (patientId) {
      url += `&patient_id=${encodeURIComponent(patientId)}`;
    }
    return axios
      .get(url, header)
      .then((res) => transformThreadMessagesResponse(res.data));
  }

  async getPendingApprovalMessages(patientId?: string): Promise<PendingMessagesResponse> {
    const header = await this.getHeaders();
    // Paginated like the reports list, and the first page can come back empty with a
    // `nextToken` while messages wait further on — the queue looked empty to coordinators
    // with a doctor's message pending. Every page is read.
    const items: any[] = [];
    let nextToken: string | undefined;
    let pages = 0;
    do {
      const params = new URLSearchParams({ mode: 'pending_messages' });
      if (patientId) params.append('patient_id', patientId);
      // the token arrives already URL-encoded; decode it so it is not encoded twice
      if (nextToken) params.append('nextToken', /%[0-9a-f]{2}/i.test(nextToken) ? decodeURIComponent(nextToken) : nextToken);
      const data = await axios.get(`${API_URL}queries?${params.toString()}`, header).then((res) => res.data);
      items.push(...(data?.pending_messages ?? data?.messages ?? []));
      nextToken = data?.nextToken || undefined;
      pages += 1;
    } while (nextToken && pages < 50);
    return transformPendingMessagesResponse({ pending_messages: items });
  }

  async startThread(patientId: string, message: string, subject?: string) {
    const header = await this.getHeaders();
    const body: Record<string, any> = { patient_id: patientId, message };
    if (subject?.trim()) body.subject = subject.trim();
    return axios
      .post(`${API_URL}/queries/thread`, body, header)
      .then((res) => res.data);
  }

  async replyToThread(threadId: string, threadPk: string, message: string) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}queries/message`, { thread_id: threadId, thread_pk: threadPk, message }, header)
      .then((res) => res.data);
  }

  async approveOrRejectMessage(payload: ApproveMessagePayload) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}queries/approve`, payload, header)
      .then((res) => res.data);
  }

  async summariseDocuments(patientId: string) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}documents/summarise`, { patient_id: patientId }, header)
      .then((res) => res.data);
  }

  async requestVideoCall(payload: {
    scheduled_time: string;
    reason?: string;
  }) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}video-calls/request`, payload, header)
      .then((res) => res.data);
  }

  async getVideoCallRequests(status: 'PENDING' | 'SCHEDULED' | 'ALL' = 'ALL') {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}video-calls/requests?status=${status}`, header)
      .then((res) => res.data);
  }

  async createVideoCall(payload: {
    request_id?: string;
    patient_id?: string;
    participants: string[];
    scheduled_time?: string;
  }) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}video-calls`, payload, header)
      .then((res) => res.data);
  }

  async getUserVideoCalls(
    userId: string = 'me',
    filter: 'upcoming' | 'pending' | 'completed' | 'all' = 'all'
  ) {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}user/${encodeURIComponent(userId)}/video-calls?filter=${filter}`, header)
      .then((res) => res.data);
  }

  async startVideoCall(meetingId: string): Promise<{ token: string; session_token: string; identity: string; room_name?: string }> {
    const header = await this.getHeaders();
    return axios
      .put(
        `${API_URL}video-calls/${encodeURIComponent(meetingId)}/start`,
        {},
        header
      )
      .then((res) => res.data);
  }

  async endVideoCall(meetingId: string) {
    const header = await this.getHeaders();
    return axios
      .put(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/end`, {}, header)
      .then((res) => res.data);
  }

  async leaveVideoCall(meetingId: string, sessionToken: string) {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/leave`, { session_token: sessionToken }, header)
      .then((res) => res.data);
  }

  async getVideoCallParticipants(meetingId: string) {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/participants`, header)
      .then((res) => res.data);
  }

  async updateVideoCallStatus(meetingId: string, status: string) {
    const header = await this.getHeaders();
    return axios
      .put(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/status`, { status }, header)
      .then((res) => res.data);
  }

  async requestRecordingUploadUrl(meetingId: string, contentType: string, fileName: string) {
    const header = await this.getHeaders();
    const data = {
      content_type: contentType,
      file_name: fileName
    };
    return axios
      .post(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/recording`, data, header)
      .then((res) => res.data);
  }

  async retrieveRecording(meetingId: string) {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/recording`, header)
      .then((res) => res.data);
  }

  async getDashboard() {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}dashboard`, header)
      .then((res) => res.data);
  }

  async getPatientCompleteSummary(patientId?: string, forceRefresh?: boolean, language?: 'en' | 'bg') {
    const header = await this.getHeaders();
    const params = new URLSearchParams();
    if (patientId) params.append('patient_id', encodeURIComponent(patientId));
    if (forceRefresh) params.append('force', 'true');
    if (language) params.append('language', language);
    const query = params.toString();
    const url = `${API_URL}patients/complete-summary${query ? `?${query}` : ''}`;
    return axios.get(url, header).then((res) => res.data);
  }

  async getPatientReportSummary(
    reportId: string,
    language?: 'en' | 'bg',
    force?: boolean,
    patientId?: string
  ): Promise<{ summary: string; report_id: string; patient_id: string; language: string; cached: boolean }> {
    const header = await this.getHeaders();
    const params = new URLSearchParams();
    params.append('report_id', reportId);
    if (language) params.append('language', language);
    if (force) params.append('force', 'true');
    if (patientId) params.append('patient_id', patientId);
    return axios
      .get(`${API_URL}patients/reports/summary?${params.toString()}`, header)
      .then((res) => res.data);
  }

  async seedDashboard() {
    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}dashboard/seed`, {}, header)
      .then((res) => res.data);
  }

  async createGhostPatient(payload: {
    email: string;
    FirstName: string;
    LastName: string;
    MiddleName?: string;
    gender: string;
    payment_status?: string;
  }, role: 'doctor' | 'nurse' = 'doctor') {
    const header = await this.getHeaders();
    const endpoint = role === 'nurse' ? 'nurse/create-patient' : 'doctor/create-patient';
    return axios
      .post(`${API_URL}${endpoint}`, payload, header)
      .then((res) => res.data);
  }

  async getGhostPatients() {
    const header = await this.getHeaders();
    return axios
      .get(`${API_URL}users?userType=patient`, header)
      .then((res) => res.data);
  }

  async generateReportDraft(patientId: string, text: string, language?: 'en' | 'bg'): Promise<{ report_draft: string; model: string; patient_id: string; language: string }> {
    const header = await this.getHeaders();
    const body: Record<string, any> = { patient_id: patientId, text };
    if (language) body.language = language;
    return axios
      .post(`${API_URL}doctor/generate-report`, body, header)
      .then((res) => res.data);
  }

  async refineDictation(rawText: string): Promise<{ refined_text: string }> {

    const header = await this.getHeaders();
    return axios
      .post(`${API_URL}doctor/refine-dictation`, { raw_text: rawText }, header)
      .then((res) => res.data);
  }

  async reviewReport(
    patientId: string,
    reportId: string,
    action: 'approve' | 'reject',
    reason: string,
    role: 'admin' | 'nurse' = 'admin'
  ): Promise<{ message: string; report_id: string; approval_status: string; reviewed_by: string; reviewed_at: string }> {
    const header = await this.getHeaders();
    const prefix = role === 'nurse' ? 'nurse' : 'admin';
    return axios
      .put(`${API_URL}${prefix}/patients/${patientId}/report/${reportId}/review`, { action, reason }, header)
      .then((res) => res.data);
  }

  async reviseReport(
    patientId: string,
    reportId: string,
    reportContent: string
  ): Promise<{ message: string; report_id: string; approval_status: string; revision_count: number; revised_at: string }> {
    const header = await this.getHeaders();
    return axios
      .put(`${API_URL}doctor/patients/${patientId}/report/${reportId}`, { report_content: reportContent }, header)
      .then((res) => res.data);
  }

  async generateReportPdf(
    reportId: string,
    patientId: string,
    branding?: {
      header?: { title?: string; subtitle?: string };
      footer?: { line1?: string; line2?: string; disclaimer?: string };
      page_footer?: { running_header?: string };
    }
  ): Promise<{ message: string; report_id: string; pdf_s3_key: string; pdf_url: string; generated_at: string }> {
    const header = await this.getHeaders();
    const body: Record<string, any> = { patient_id: patientId };
    if (branding?.header && Object.keys(branding.header).length) body.header = branding.header;
    if (branding?.footer && Object.keys(branding.footer).length) body.footer = branding.footer;
    if (branding?.page_footer && Object.keys(branding.page_footer).length) body.page_footer = branding.page_footer;
    return axios
      .post(`${API_URL}doctor/reports/${reportId}/generate-pdf`, body, header)
      .then((res) => res.data);
  }

  // ─── Consent ledger (DPDP Act 2023 / DPDP Rules 2025) ────────────────────
  //
  // These hit a DEDICATED append-only store, not `patients/update_details`.
  // See ConsentTypes.ts for why the profile document cannot hold consent.

  /**
   * Append a consent artefact. Called every time the patient makes or changes
   * consent choices — never an update-in-place.
   */
  async recordConsent(payload: ConsentArtefactRequest): Promise<RecordConsentResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}consents/record`, payload, header)
      .then((res) => res.data);
  }

  /**
   * Current effective consent state plus the full audit trail.
   *
   * `patientId` is ONLY for staff reading a patient's ledger. A patient must
   * never pass their own id — the server reads identity from the token and
   * rejects any patient-sent id with "Patients may not query other patients'
   * consents." Callers decide by role; see ConsentSettingsPanel.
   *
   * Staff may READ the ledger; only the Data Principal can write it.
   */
  async getConsentState(patientId?: string): Promise<ConsentStateResponse> {
    const header = await this.getHeaders();
    const query = patientId ? `?patient_id=${encodeURIComponent(patientId)}` : '';

    return axios
      .get(`${API_URL}consents${query}`, header)
      .then((res) => res.data);
  }

  /**
   * Withdraw one or more consents — DPDP s.6(4)-(6).
   *
   * Withdrawal must be as easy as granting, so this takes no confirmation
   * ceremony beyond the UI's own. The response reports what the backend did as
   * a consequence.
   */
  async withdrawConsent(payload: WithdrawConsentRequest): Promise<WithdrawConsentResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}consents/withdraw`, payload, header)
      .then((res) => res.data);
  }

  /**
   * Human-readable consent receipt for one artefact, for the patient to keep.
   */
  async getConsentReceipt(artefactId: string) {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}consents/receipt/${encodeURIComponent(artefactId)}`, header)
      .then((res) => res.data);
  }

  /**
   * Record the specialty the patient chose, BEFORE payment is attempted.
   *
   * Without this the selection lives only in React state and is inferred later
   * from the Stripe price_id, so an abandoned checkout leaves no trace and the
   * consent, the selection and the payment are never provably linked.
   */
  async recordServiceSelection(payload: ServiceSelectionRequest): Promise<ServiceSelectionResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}patients/service-selection`, payload, header)
      .then((res) => res.data);
  }

  async getServiceSelection(patientId?: string): Promise<ServiceSelectionResponse | null> {
    const header = await this.getHeaders();
    const query = patientId ? `?patient_id=${encodeURIComponent(patientId)}` : '';

    return axios
      .get(`${API_URL}patients/service-selection${query}`, header)
      .then((res) => res.data ?? null);
  }

  /**
   * Record a cookie / client-storage consent choice.
   *
   * UNAUTHENTICATED by design — the banner is shown before sign-in, so this is
   * the one endpoint that must not send an auth token. It is keyed on a
   * per-browser anonymous id instead.
   */
  async recordCookieConsent(payload: CookieConsentRequest): Promise<CookieConsentResponse> {
    return axios
      .post(`${API_URL}consents/cookies`, payload, {
        headers: { 'Content-Type': 'application/json' },
      })
      .then((res) => res.data);
  }

  /**
   * Read the cookie consent attached to the signed-in account.
   *
   * Authenticated, unlike the write — the write happens pre-login and is keyed
   * on an anonymous id, but reading someone's stored choice must require being
   * that someone. There is deliberately no anonymous read: it would let anyone
   * holding a guessed id read another visitor's preferences, and it would buy
   * nothing, since a browser that has the anonymous id also has the local copy.
   */
  async getCookieConsent(): Promise<CookieConsentStateResponse> {
    const header = await this.getHeaders();

    return axios
      .get(`${API_URL}consents/cookies`, header)
      .then((res) => res.data);
  }

  /**
   * Attach a pre-login cookie consent to the now-signed-in account, so the
   * patient's audit trail is one chain rather than an anonymous record and an
   * account record that nobody can connect.
   */
  async linkAnonymousCookieConsent(anonymousId: string) {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}consents/cookies/link`, { anonymousId }, header)
      .then((res) => res.data);
  }

  // ─── Notifications ────────────────────────────────────────────────────────
  //
  // Multi-role: the backend scopes rows to the caller's own identity from the
  // token, so there is no user id parameter and no way to read someone else's.

  /**
   * Fetch notifications for the signed-in user.
   *
   * `unreadCount` is computed server-side across ALL rows, not just the page
   * returned, so it stays correct regardless of `limit`.
   */
  async getNotifications(params: { unreadOnly?: boolean; limit?: number } = {}): Promise<NotificationsResponse> {
    const header = await this.getHeaders();
    const query = new URLSearchParams();
    if (params.unreadOnly) query.append('unreadOnly', 'true');
    if (params.limit !== undefined) query.append('limit', String(params.limit));
    const qs = query.toString();

    return axios
      .get(`${API_URL}notifications${qs ? `?${qs}` : ''}`, header)
      .then((res) => res.data);
  }

  async markNotificationRead(notifId: string): Promise<MarkNotificationReadResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}notifications/${encodeURIComponent(notifId)}/read`, {}, header)
      .then((res) => res.data);
  }

  async markAllNotificationsRead(): Promise<MarkAllNotificationsReadResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}notifications/read-all`, {}, header)
      .then((res) => res.data);
  }

  // ─── Admin: doctor & nurse lifecycle ──────────────────────────────────────

  /** Partial update — send only the fields that changed. */
  async updateDoctor(doctorId: string, payload: UpdateDoctorPayload): Promise<UpdateStaffResponse> {
    const header = await this.getHeaders();

    return axios
      .patch(`${API_URL}admin/doctors/${encodeURIComponent(doctorId)}`, payload, header)
      .then((res) => res.data);
  }

  /**
   * Deactivate a doctor.
   *
   * Throws on 409 when the doctor still holds active patients; the error body
   * carries `blockedByPatients` so the caller can name them. See
   * `DeactivateBlockedError`.
   */
  async deactivateDoctor(doctorId: string, reason: string): Promise<DeactivateStaffResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}admin/doctors/${encodeURIComponent(doctorId)}/deactivate`, { reason }, header)
      .then((res) => res.data);
  }

  async updateNurse(nurseId: string, payload: UpdateNursePayload): Promise<UpdateStaffResponse> {
    const header = await this.getHeaders();

    return axios
      .patch(`${API_URL}admin/nurses/${encodeURIComponent(nurseId)}`, payload, header)
      .then((res) => res.data);
  }

  /** Same 409 soft-block semantics as `deactivateDoctor`. */
  async deactivateNurse(nurseId: string, reason: string): Promise<DeactivateStaffResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}admin/nurses/${encodeURIComponent(nurseId)}/deactivate`, { reason }, header)
      .then((res) => res.data);
  }

  // ─── Support tickets (staff queue) ────────────────────────────────────────

  /**
   * List all tickets. Staff-only — patients use `getSupportTickets`, which
   * returns just their own.
   */
  async listSupportTickets(params: { status?: SupportTicketStatus; limit?: number } = {}): Promise<SupportTicketListResponse> {
    const header = await this.getHeaders();
    const query = new URLSearchParams();
    if (params.status) query.append('status', params.status);
    if (params.limit !== undefined) query.append('limit', String(params.limit));
    const qs = query.toString();

    return axios
      .get(`${API_URL}support/tickets${qs ? `?${qs}` : ''}`, header)
      .then((res) => res.data);
  }

  /** Replying to an OPEN ticket auto-advances it to IN_PROGRESS server-side. */
  async replyToSupportTicket(ticketId: string, message: string): Promise<SupportTicketReplyResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}support/tickets/${encodeURIComponent(ticketId)}/reply`, { message }, header)
      .then((res) => res.data);
  }

  async updateSupportTicket(ticketId: string, payload: UpdateSupportTicketPayload): Promise<UpdateSupportTicketResponse> {
    const header = await this.getHeaders();

    return axios
      .patch(`${API_URL}support/tickets/${encodeURIComponent(ticketId)}`, payload, header)
      .then((res) => res.data);
  }

  // ─── Video call lifecycle ─────────────────────────────────────────────────

  /**
   * Cancel a scheduled call. The backend emails the patient and raises a
   * `video_cancelled` notification. Throws 409 if already ENDED or CANCELLED.
   */
  async cancelVideoCall(meetingId: string, reason: string): Promise<CancelVideoCallResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/cancel`, { reason }, header)
      .then((res) => res.data);
  }

  /**
   * Move a call to a new time. The backend rebuilds the reminder, emails all
   * participants with old vs new, and raises `video_rescheduled`.
   *
   * `scheduledTime` must be an ISO-8601 string.
   */
  async rescheduleVideoCall(meetingId: string, scheduledTime: string): Promise<RescheduleVideoCallResponse> {
    const header = await this.getHeaders();

    return axios
      .post(`${API_URL}video-calls/${encodeURIComponent(meetingId)}/reschedule`, { scheduledTime }, header)
      .then((res) => res.data);
  }
}

const ApiServices = new APIService();
export default ApiServices;
